import styled from '@emotion/styled'
import { AdUnit } from '@pubstack/common/src/adunit'
import { DeviceType } from '@pubstack/common/src/device-type'
import { AdUnitConfig, isStackNameReserved, RoadblockType } from '@pubstack/common/src/stack'
import { FunctionComponent, useMemo } from 'react'
import { Controller, FieldValue, useFormContext, Validate } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Skeleton } from '~/components/Skeleton'
import { useToast } from '~/components/Toast/useToasts'
import { WithClassName } from '~/types/utils'
import { AdUnitCard } from './components/AdUnitCard'
import { PureStackSelectAdUnitsFlyout } from './PureStackSelectAdUnitsFlyout'
import { StackFormData } from './StackEdit'

const Title = styled.h2`
  ${Fonts.H2};
  ${Colors.Jet};
  font-weight: 500;
  margin-bottom: 16px;
`

const Subtitle = styled.div`
  ${Fonts.P1};
  ${Colors.Jet};
  margin-bottom: 20px;
`
const IdentificationWrapper = styled.div`
  display: inline-flex;
  gap: 15px;
  align-items: baseline;
  padding-bottom: 32px;
  & > ${BaseInputElement} {
    width: 350px;
  }
`
const DesktopWrapper = styled.div`
  margin-bottom: 32px;
`

const MobileWrapper = styled.div`
  margin-bottom: 24px;
`

const AdUnitsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, calc(25% - 8px));
  gap: 10px;
  margin-bottom: 14px;
`

const ButtonErrorWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  ${Button} + ${Button} {
    margin-left: 8px;
  }
`
const ErrorMessage = styled.span`
  ${Fonts.P2};
  display: inline-flex;
  align-items: center;
  ${Fonts.colors.Alert};

  > * {
    margin-right: 4px;
  }
`

type PureStackAdUnitSelectionStepProps = WithClassName & {
  selectableAdUnits: AdUnit[]
  contextOptions: SelectOptionProp[]
  stackName: string
  isEditing: boolean
  isLoading: boolean
  validateUniqueName: (name: string) => Promise<boolean>
}

type FullAdUnitConfig = { adUnit: AdUnit; roadblock?: RoadblockType }

const adUnitCards = (onChange: (event: AdUnitConfig[]) => void, adUnitsConfigs: FullAdUnitConfig[]) => {
  if (!adUnitsConfigs.length) {
    return undefined
  }
  return (
    <AdUnitsWrapper>
      {adUnitsConfigs.map((adUnitConfig) => {
        return (
          <AdUnitCard
            key={adUnitConfig.adUnit.id}
            isError={isDuplicate(adUnitConfig.adUnit, adUnitsConfigs) || isArchived(adUnitConfig.adUnit)}
            isArchived={isArchived(adUnitConfig.adUnit)}
            adUnit={adUnitConfig.adUnit}
            onClose={() => {
              onChange(adUnitsConfigs.filter((c) => c.adUnit !== adUnitConfig.adUnit).map((c) => ({ adUnitId: c.adUnit.id, roadblock: c.roadblock })))
            }}
            roadblock={{
              isRoadblock: !!adUnitConfig.roadblock,
              onToggleClick: () => {
                onChange(
                  adUnitsConfigs.map((config) => {
                    if (config.adUnit.id === adUnitConfig.adUnit.id) {
                      return { adUnitId: config.adUnit.id, roadblock: config.roadblock ? undefined : RoadblockType.Companion }
                    } else {
                      return { adUnitId: config.adUnit.id, roadblock: config.roadblock }
                    }
                  })
                )
              },
              isMaster: adUnitConfig.roadblock === RoadblockType.Master,
              onMasterIconClick: () => {
                onChange(
                  adUnitsConfigs.map((config) => {
                    if (config.adUnit.id === adUnitConfig.adUnit.id) {
                      const roadblock = config.roadblock === RoadblockType.Master ? RoadblockType.Companion : RoadblockType.Master
                      return { adUnitId: config.adUnit.id, roadblock }
                    } else {
                      return { adUnitId: config.adUnit.id, roadblock: config.roadblock === RoadblockType.Master ? RoadblockType.Companion : config.roadblock }
                    }
                  })
                )
              },
            }}
          />
        )
      })}
    </AdUnitsWrapper>
  )
}

const isDuplicate = (adUnit: AdUnit, adUnits: FullAdUnitConfig[]): boolean => adUnits.filter((a) => a.adUnit.divId === adUnit.divId).length > 1
const isArchived = (adUnit: AdUnit): boolean => !adUnit.enabled

/**
 * Control duplicates in given adUnits collection
 * Rules :
 * - Unique div ID
 * - Unique ad server ad unit name
 * @param adUnits
 * @return the potential error, empty otherwise
 */
const findDuplicates = (adUnits: AdUnit[]): string | undefined => {
  const divIdsArr = adUnits.map((adUnit) => adUnit.divId)
  const isDivIdDuplicate = new Set(divIdsArr).size !== divIdsArr.length

  return isDivIdDuplicate ? `Some ad units have the same div ID. You can keep only one of each.` : undefined
}

const validateRoadblock = (adUnits: AdUnitConfig[]): string | true => {
  const roadblockAdUnits = adUnits.filter((adUnit) => adUnit.roadblock)
  if (roadblockAdUnits.length === 0) {
    return true
  }
  if (roadblockAdUnits.length < 2) {
    return 'A roadblock should contain at least two ad units'
  } else {
    return adUnits.filter((adUnit) => adUnit.roadblock === RoadblockType.Master).length === 0 ? "An ad unit must be selected as the roadblock's master" : true
  }
}

const findArchivedAdUnits = (adUnits: AdUnit[]): string | true => {
  return adUnits.find((adUnit) => isArchived(adUnit)) ? `Some ad units are now archived. Please remove them.` : true
}

const UnstyledPureStackAdUnitSelectionStep: FunctionComponent<PureStackAdUnitSelectionStepProps> = ({
  className,
  selectableAdUnits,
  contextOptions,
  stackName,
  isEditing,
  isLoading,
  validateUniqueName,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<StackFormData>()
  const adUnitsById = useMemo(() => selectableAdUnits.reduce((acc, adunit) => ({ ...acc, [adunit.id]: adunit }), {} as { [key: string]: AdUnit }), [selectableAdUnits])
  const toAdUnitsConfig = (configs: AdUnitConfig[] | undefined) =>
    (configs || []).map((config) => {
      return { adUnit: adUnitsById[config.adUnitId], roadblock: config.roadblock }
    })
  const toast = useToast()
  const flyout = useGlobalFlyout()

  const openFlyout = (onChange: (event: AdUnitConfig[]) => void, adUnitConfigs: FullAdUnitConfig[], flyoutType: DeviceType) =>
    flyout.open(
      PureStackSelectAdUnitsFlyout,
      { color: Colors.Lilac, width: '935px', closeTrackingLabel: `adm-sites-stacks-form-select-${flyoutType}-ad-units-view-close` },
      {
        title: `Select ${flyoutType} ad units`,
        onValidate: (newAdUnits: AdUnit[]) => {
          const roadblockByAdUnitId = adUnitConfigs.reduce<Record<string, RoadblockType | undefined>>((acc, config) => ({ ...acc, [config.adUnit.id]: config.roadblock }), {})
          onChange(newAdUnits.map((adUnit) => ({ adUnitId: adUnit.id, roadblock: roadblockByAdUnitId[adUnit.id] })))
          flyout.close()
          const error = findDuplicates(newAdUnits)
          if (error) {
            toast.alert(error)
          }
        },
        selectableAdUnits: selectableAdUnits.filter((adUnit) => adUnit.enabled && adUnit.devices.includes(flyoutType)),
        selectedAdUnits: adUnitConfigs.map((c) => c.adUnit),
      }
    )

  const validateName: Validate<FieldValue<StackFormData>, StackFormData> = async (value) => {
    const parsedValue = (Array.isArray(value) ? value.join() : value) as string
    if (isStackNameReserved(parsedValue)) {
      return `The name can't be "Default".`
    }
    if (!isEditing) {
      const isUnique = await validateUniqueName(parsedValue)
      if (!isUnique) {
        return 'This name is already in use.'
      }
    }
    return true
  }

  return (
    <div className={className}>
      <>
        <Title>Identification</Title>
        {isLoading ? (
          <Skeleton bigger width={'100px'} />
        ) : isStackNameReserved(stackName) ? (
          <IdentificationWrapper>
            <Input name={'name'} control={control} labelIsPlaceholder label={'Name'} disabled={true} />
            <Select
              options={[
                { label: 'Default', value: null, trackingLabel: 'adm-sites-stacks-form-default-context-set' },
                { label: 'Default', value: undefined, trackingLabel: 'adm-sites-stacks-form-default-context-set' },
              ]}
              control={control}
              label={'Context'}
              name={`configuration.contextId`}
              disabled={true}
            />
          </IdentificationWrapper>
        ) : (
          <IdentificationWrapper>
            <Input
              name={'name'}
              error={errors?.name?.message}
              control={control}
              rules={{
                required: { value: true, message: 'Name required.' },
                validate: validateName,
              }}
              labelIsPlaceholder
              label={'Name'}
              disabled={isEditing}
            />
            <Select
              options={contextOptions}
              error={errors?.configuration?.contextId?.message}
              label={'Context'}
              control={control}
              name={`configuration.contextId`}
              rules={{
                required: 'Context required.',
              }}
              searchable={true}
            />
          </IdentificationWrapper>
        )}
        <Title>Target</Title>
      </>
      <Subtitle>Select the ad units to use on the stack.</Subtitle>
      <DesktopWrapper>
        <Title>Desktop ad units</Title>
        {isLoading ? (
          <Skeleton bigger width={'242px'} />
        ) : (
          <Controller
            control={control}
            name={'configuration.desktopAdUnits'}
            rules={{
              required: 'Select at least one ad unit per device.',
              validate: {
                noDuplicate: (adUnits) => findDuplicates(toAdUnitsConfig(adUnits).map((c) => c.adUnit)),
                roadblock: (adUnits) => validateRoadblock(adUnits ?? []),
                noArchivedAdUnit: (adUnits) => findArchivedAdUnits(toAdUnitsConfig(adUnits).map((c) => c.adUnit)),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <>
                {adUnitCards(onChange, toAdUnitsConfig(value))}
                <ButtonErrorWrapper>
                  <Button variant={'secondary'} onClick={() => openFlyout(onChange, toAdUnitsConfig(value), 'desktop')} trackingLabel={'adm-sites-stacks-form-select-desktop-ad-units-view'}>
                    Select ad units
                  </Button>
                  {(value || []).length > 0 && (
                    <Button variant={'tertiary'} onClick={() => onChange([])} trackingLabel={'adm-sites-stacks-form-remove-all-desktop-ad-units-apply'}>
                      Remove all
                    </Button>
                  )}
                  {errors.configuration?.desktopAdUnits?.message && (
                    <ErrorMessage>
                      <Icon name={'alert'} width={'0.875rem'} /> {errors.configuration?.desktopAdUnits?.message}
                    </ErrorMessage>
                  )}
                </ButtonErrorWrapper>
              </>
            )}
          />
        )}
      </DesktopWrapper>
      <MobileWrapper>
        <Title>Mobile ad units</Title>
        {isLoading ? (
          <Skeleton bigger width={'242px'} />
        ) : (
          <Controller
            control={control}
            name={'configuration.mobileAdUnits'}
            rules={{
              required: 'Select at least one ad unit per device.',
              validate: {
                noDuplicate: (adUnits) => findDuplicates(toAdUnitsConfig(adUnits).map((c) => c.adUnit)),
                roadblock: (adUnits) => validateRoadblock(adUnits ?? []),
                noArchivedAdUnit: (adUnits) => findArchivedAdUnits(toAdUnitsConfig(adUnits).map((c) => c.adUnit)),
              },
            }}
            render={({ field: { onChange, value } }) => (
              <>
                {adUnitCards(onChange, toAdUnitsConfig(value))}
                <ButtonErrorWrapper>
                  <Button variant={'secondary'} onClick={() => openFlyout(onChange, toAdUnitsConfig(value), 'mobile')} trackingLabel={'adm-sites-stacks-form-select-mobile-ad-units-view'}>
                    Select ad units
                  </Button>
                  {(value || []).length > 0 && (
                    <Button variant={'tertiary'} onClick={() => onChange([])} trackingLabel={'adm-sites-stacks-form-remove-all-mobile-ad-units-apply'}>
                      Remove all
                    </Button>
                  )}
                  {errors.configuration?.mobileAdUnits?.message && (
                    <ErrorMessage>
                      <Icon name={'alert'} width={'0.875rem'} /> {errors.configuration.mobileAdUnits?.message}
                    </ErrorMessage>
                  )}
                </ButtonErrorWrapper>
              </>
            )}
          />
        )}
      </MobileWrapper>
    </div>
  )
}
export const PureStackAdUnitSelectionStep = styled(UnstyledPureStackAdUnitSelectionStep)``
